// Route table — replaces Next.js's file-based routing from `src/pages/`.
// Routes mirror the existing URL shapes exactly so Cognito callback
// URLs, S3 routing rules, CloudFront cache keys, bookmarks, and
// external links keep working bit-for-bit after the cutover.
//
// Conventions inherited from Next:
//   - `pages/index.tsx`             → `/`
//   - `pages/foo.tsx`               → `/foo`
//   - `pages/foo/index.tsx`         → `/foo`
//   - `pages/foo/bar.tsx`           → `/foo/bar`
//   - `pages/404.tsx`               → catch-all (* in React Router)
//
// Layout pattern: DashboardLayout is a persistent parent <Route> that
// wraps all authenticated pages via React Router's <Outlet>. This means
// the layout never unmounts on navigation — no per-page getLayout
// assignments, no AuthGuard remounting on every route change.
//
// Trailing slash: React Router 6 treats `/foo` and `/foo/` as the same
// route for matching so client-side nav works either way.
import React, { lazy } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { Layout as DashboardLayout } from 'layouts/dashboard/layout';
import { Layout as AuthLayout } from 'layouts/auth/layout';

const lazy$ = (loader: () => Promise<{ default: React.ComponentType<any> }>) => lazy(loader);

const HomePage = lazy$(() => import('pages/index'));
const NotFoundPage = lazy$(() => import('pages/404'));
const ForbiddenPage = lazy$(() => import('pages/forbidden'));
const MaintenancePage = lazy$(() => import('pages/maintenance'));
const ManualNotFoundPage = lazy$(() => import('pages/notfound'));

const LoginPage = lazy$(() => import('pages/auth/login'));
const BookmarksListPage = lazy$(() => import('pages/bookmarks/list'));
const CheckpointIndexPage = lazy$(() => import('pages/checkpoint/index'));
const CheckpointReviewPage = lazy$(() => import('pages/checkpoint/review'));

const ClientsAddPage = lazy$(() => import('pages/clients/add'));
const ClientsDetailsPage = lazy$(() => import('pages/clients/details'));
const ClientsListPage = lazy$(() => import('pages/clients/list'));

const CompanyMonthlyPage = lazy$(() => import('pages/company_monthly/index'));

const DashboardsIndexPage = lazy$(() => import('pages/dashboards/index'));
const DashboardsProjectCheatSheetPage = lazy$(() => import('pages/dashboards/project-cheat-sheet'));
const DashboardsAvailablePeoplePage = lazy$(() => import('pages/dashboards/available-people'));
const DashboardsInvoiceReportPage = lazy$(() => import('pages/dashboards/invoice-report'));

const DepartmentsAddPage = lazy$(() => import('pages/departments/add'));
const DepartmentsDetailsPage = lazy$(() => import('pages/departments/details'));
const DepartmentsListPage = lazy$(() => import('pages/departments/list'));
const DepartmentsUtilisationPage = lazy$(() => import('pages/departments/utilisation'));

const DocumentsDetailsPage = lazy$(() => import('pages/documents/details'));
const DocumentsListPage = lazy$(() => import('pages/documents/list'));

const Internal5Page = lazy$(() => import('pages/internal/internal_5'));
const InternalFinancePage = lazy$(() => import('pages/internal/internal_finance'));
const InternalHolidaysPage = lazy$(() => import('pages/internal/internal_holidays'));
const InternalIndexPage = lazy$(() => import('pages/internal/internal_index'));
const InternalSmarttimeBobPage = lazy$(() => import('pages/internal/internal_smarttime_bob'));
const InternalUnallocatedPeoplePage = lazy$(() => import('pages/internal/internal_unallocated_people'));

const PeopleAddPage = lazy$(() => import('pages/people/add'));
const PeopleDetailsPage = lazy$(() => import('pages/people/details'));
const PeopleListPage = lazy$(() => import('pages/people/list'));
const PeopleSkillSearchPage = lazy$(() => import('pages/people/skill-search'));
const PeopleMapPage = lazy$(() => import('pages/people_map/index'));

const PitchesListPage = lazy$(() => import('pages/pitches/list'));
const FutureJoinersListPage = lazy$(() => import('pages/future-joiners/list'));
const FutureJoinerDetailsPage = lazy$(() => import('pages/future-joiners/details'));
const ProfileGeneratorPage = lazy$(() => import('pages/profile-generator'));
const LlmFeedbackPage = lazy$(() => import('pages/llm-feedback'));
const AiCreditsPage = lazy$(() => import('pages/ai-credits'));

const ProjectsAddPage = lazy$(() => import('pages/projects/add'));
const ProjectsDetailsPage = lazy$(() => import('pages/projects/details'));
const ProjectsListPage = lazy$(() => import('pages/projects/list'));
const ProjectTransferPage = lazy$(() => import('pages/projects/transfer'));

const SettingsPage = lazy$(() => import('pages/settings/settings'));
const SkillsProposalsDetailsPage = lazy$(() => import('pages/skills/proposals/details'));

const TasksListPage = lazy$(() => import('pages/tasks/list'));

// Auth layout wrapper — used only for the login page.
const AuthLayoutWrapper = () => <AuthLayout><Outlet /></AuthLayout>;

export const AppRoutes = () => (
  <Routes>
    {/* Auth pages — no dashboard chrome */}
    <Route element={<AuthLayoutWrapper />}>
      <Route path="/auth/login" element={<LoginPage />} />
    </Route>

    {/* Standalone pages — no layout (maintenance/forbidden may show pre-auth) */}
    <Route path="/forbidden" element={<ForbiddenPage />} />
    <Route path="/maintenance" element={<MaintenancePage />} />

    {/* Dashboard — persistent layout parent. DashboardLayout renders
          <Outlet /> so it never unmounts on navigation; AuthGuard mounts
          once and checked stays true for the session lifetime. */}
    <Route element={<DashboardLayout />}>
      <Route path="/" element={<HomePage />} />

      <Route path="/bookmarks/list" element={<BookmarksListPage />} />

      <Route path="/checkpoint" element={<CheckpointIndexPage />} />
      <Route path="/checkpoint/review" element={<CheckpointReviewPage />} />

      <Route path="/clients/list" element={<ClientsListPage />} />
      <Route path="/clients/add" element={<ClientsAddPage />} />
      <Route path="/clients/details" element={<ClientsDetailsPage />} />

      <Route path="/company_monthly" element={<CompanyMonthlyPage />} />

      <Route path="/dashboards" element={<DashboardsIndexPage />} />
      <Route path="/dashboards/project-cheat-sheet" element={<DashboardsProjectCheatSheetPage />} />
      <Route path="/dashboards/available-people" element={<DashboardsAvailablePeoplePage />} />
      <Route path="/dashboards/invoice-report" element={<DashboardsInvoiceReportPage />} />

      <Route path="/departments/list" element={<DepartmentsListPage />} />
      <Route path="/departments/add" element={<DepartmentsAddPage />} />
      <Route path="/departments/details" element={<DepartmentsDetailsPage />} />
      <Route path="/departments/utilisation" element={<DepartmentsUtilisationPage />} />

      <Route path="/documents/list" element={<DocumentsListPage />} />
      <Route path="/documents/details" element={<DocumentsDetailsPage />} />

      <Route path="/internal/internal_index" element={<InternalIndexPage />} />
      <Route path="/internal/internal_5" element={<Internal5Page />} />
      <Route path="/internal/internal_finance" element={<InternalFinancePage />} />
      <Route path="/internal/internal_holidays" element={<InternalHolidaysPage />} />
      <Route path="/internal/internal_smarttime_bob" element={<InternalSmarttimeBobPage />} />
      <Route path="/internal/internal_unallocated_people" element={<InternalUnallocatedPeoplePage />} />

      <Route path="/people/list" element={<PeopleListPage />} />
      <Route path="/people/add" element={<PeopleAddPage />} />
      <Route path="/people/details" element={<PeopleDetailsPage />} />
      <Route path="/people/skill-search" element={<PeopleSkillSearchPage />} />
      <Route path="/people_map" element={<PeopleMapPage />} />

      <Route path="/pitches/list" element={<PitchesListPage />} />
      <Route path="/future-joiners/list" element={<FutureJoinersListPage />} />
      <Route path="/future-joiners/details" element={<FutureJoinerDetailsPage />} />
      <Route path="/profile-generator" element={<ProfileGeneratorPage />} />
      <Route path="/llm-feedback" element={<LlmFeedbackPage />} />
      <Route path="/ai-credits" element={<AiCreditsPage />} />

      <Route path="/projects/list" element={<ProjectsListPage />} />
      <Route path="/projects/add" element={<ProjectsAddPage />} />
      <Route path="/projects/details" element={<ProjectsDetailsPage />} />
      <Route path="/projects/transfer" element={<ProjectTransferPage />} />

      <Route path="/settings/settings" element={<SettingsPage />} />

      <Route path="/skills/proposals/details" element={<SkillsProposalsDetailsPage />} />

      <Route path="/tasks/list" element={<TasksListPage />} />

      <Route path="/notfound" element={<ManualNotFoundPage />} />
    </Route>

    {/* Catch-all — redirects to /notfound which renders inside the layout */}
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);
